// Cloudflare Turnstile — client token holder.
//
// Dark by default: when NEXT_PUBLIC_TURNSTILE_SITEKEY is unset, isTurnstileEnabled()
// is false, no widget renders, and getTurnstileToken() returns null — so the
// payment/chat API calls attach no token and nothing changes. The backend
// verifier is likewise a no-op until its secret is set (see
// apps/backend/app/integrations/turnstile.py). Activation is a deliberate,
// staged rollout (set keys -> monitor -> enforce), never just "on".
//
// The token is held module-level so the API call sites can read the latest
// solved token at request time without threading it through every component.

import { env } from "@/env";

let currentToken: string | null = null;
let tokenWaiters: Array<(token: string) => void> = [];

export function turnstileSiteKey(): string | undefined {
  return env.NEXT_PUBLIC_TURNSTILE_SITEKEY || undefined;
}

export function isTurnstileEnabled(): boolean {
  return Boolean(turnstileSiteKey());
}

export function setTurnstileToken(token: string | null): void {
  currentToken = token;
  if (token && tokenWaiters.length > 0) {
    const pending = tokenWaiters;
    tokenWaiters = [];
    for (const resolve of pending) resolve(token);
  }
}

/** Latest Turnstile token, or null when dark / not yet solved / expired. */
export function getTurnstileToken(): string | null {
  return currentToken;
}

/**
 * Resolve with a token once the widget has solved one; resolve with null when
 * Turnstile is dark or after `timeoutMs` (fail-open — the request goes out
 * token-less and the backend's monitor/enforce policy decides).
 *
 * Exists because the chat surface auto-sends the seeker's chosen question ON
 * MOUNT, which races the widget: on 2026-06-11 enforcement went live and every
 * auto-sent first message was "definitively missing" a token → 403 → silent
 * dead chat (73% of turns blocked). Any send fired at page-mount time must
 * await this first.
 */
export function waitForTurnstileToken(timeoutMs = 4000): Promise<string | null> {
  if (!isTurnstileEnabled() || currentToken) return Promise.resolve(currentToken);
  return new Promise((resolve) => {
    const onToken = (token: string): void => {
      clearTimeout(timer);
      resolve(token);
    };
    const timer = setTimeout(() => {
      tokenWaiters = tokenWaiters.filter((waiter) => waiter !== onToken);
      resolve(null);
    }, timeoutMs);
    tokenWaiters.push(onToken);
  });
}
